import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import { Button, CardV2 as Card, openMultiStepModal, showError, toast } from '@feathr/components';
import { useFundraiser } from '@feathr/extender/hooks';
import crmIcon from '@feathr/extender/images/integrations/crm_icon.svg';
import { useAccount, useLocalUrl, useStore } from '@feathr/extender/state';

import ConnectState from '../ConnectState';
import { useCRMConnectModal } from './CRMConnectModal';
import getCRMConnectSteps from './CRMConnectModal/';
import { logos } from './CRMConnectModal/assets';

import * as styles from './CRMConnectCard.css';

function CRMConnectCard(): JSX.Element {
  const { t } = useTranslation();
  const account = useAccount();
  const location = useLocation();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const { BlackbaudRaisersEdgeIntegrations } = useStore();
  const { openModal, closeModal, isOpen, initialStep } = useCRMConnectModal();
  const { fundraiser, isPending, state: stripeState, crm, disconnectCRM } = useFundraiser();

  // Check for active Blackbaud integration
  const blackbaudIntegrations = BlackbaudRaisersEdgeIntegrations.list();
  const isBlackbaudLoading = blackbaudIntegrations.isPending;
  const blackbaudIntegration =
    blackbaudIntegrations.models.length > 0 ? blackbaudIntegrations.models[0] : undefined;
  const isBlackbaudConnected = blackbaudIntegration?.isConnected === true;
  const isBlackbaudSyncActive = blackbaudIntegration?.get('upsync_activities')?.donations === true;

  // Determine the active CRM vendor and state
  const crmVendor = isBlackbaudConnected ? 'BlackbaudCRM' : crm?._cls;
  const crmState = isBlackbaudConnected ? 'connected' : crm?.state;
  const crmActive = crmState === 'connected';
  const hasCRMLicense = account.isPlatformEssential || account.isPlatformAdvanced;

  function getFormalNameCRM(): string {
    if (crmVendor === 'BlackbaudCRM') {
      return t("Blackbaud Raiser's Edge NXT");
    }
    if (crmVendor === 'SalesforceCRM') {
      return t('Salesforce');
    }
    return t('Unknown CRM');
  }

  function clearModalUrlParams(): void {
    const newParams = new URLSearchParams(location.search);
    newParams.delete('modal');
    newParams.delete('step');
    newParams.delete('crmType');
    const newSearch = newParams.toString();
    const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
    history.replace(newUrl);
  }

  function handleCloseModal(): void {
    clearModalUrlParams();
    closeModal();
  }

  async function onComplete(): Promise<void> {
    if (!crmVendor) {
      return;
    }
    try {
      // Blackbaud OAuth and sync are handled in the modal steps, so no PATCH needed here
      if (crmVendor === 'BlackbaudCRM') {
        // Refresh the cache so the Settings page shows fresh data
        BlackbaudRaisersEdgeIntegrations.clearApiCache();
      } else if (crm) {
        // Salesforce: PATCH the CRM fields
        await fundraiser?.patchIntegration({
          // Only PATCH the necessary CRM fields
          crm: {
            _cls: crm._cls,
            client_id: crm.client_id,
            client_secret: crm.client_secret,
            domain: crm.domain,
          },
        });
      }

      const successMessage =
        crmVendor === 'BlackbaudCRM'
          ? t("Blackbaud Raiser's Edge NXT connected")
          : t('{{- crmMessage}} connected', { crmMessage: getFormalNameCRM() });

      toast(successMessage, { type: 'success' });
    } catch (error) {
      showError(error, t);
    } finally {
      handleCloseModal();
    }
  }

  async function handleOnClickDisconnect(): Promise<void> {
    if (!crm) {
      return;
    }
    try {
      disconnectCRM();
      toast(
        t('{{- crmMessage}} disconnected', {
          crmMessage: getFormalNameCRM(),
        }),
        { type: 'success' },
      );
    } catch (error) {
      showError(error, t);
    }
  }

  const steps = useMemo(
    () =>
      getCRMConnectSteps({
        fundraiser,
        t,
        BlackbaudRaisersEdgeIntegrations,
        accountId: account.id,
      }),
    [
      fundraiser,
      fundraiser?.isDirty,
      fundraiser?.toJS(),
      BlackbaudRaisersEdgeIntegrations,
      account.id,
    ],
  );

  function handleStepChange(step: number): void {
    const newParams = new URLSearchParams(location.search);
    newParams.set('modal', 'crm');
    newParams.set('step', String(step));
    history.replace(`${location.pathname}?${newParams.toString()}`);
  }

  useEffect(() => {
    if (isOpen) {
      openMultiStepModal(steps, onComplete, handleCloseModal, initialStep, handleStepChange);
    }
  }, [isOpen, onComplete, handleCloseModal, initialStep]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpenModal = params.get('modal') === 'crm';
    const stepParam = params.get('step');
    const step = stepParam ? Number.parseInt(stepParam, 10) : 0;
    const crmType = params.get('crmType');

    // If Blackbaud is connected and we're NOT on the sync toggle step (step 3),
    // clear the URL params since the early steps don't apply
    const isOnSyncToggleStep = step === 3;
    if (shouldOpenModal && isBlackbaudConnected && !isOnSyncToggleStep) {
      clearModalUrlParams();
      return;
    }

    // For step 3, wait for Blackbaud data to load before proceeding
    if (isOnSyncToggleStep && isBlackbaudLoading) {
      return;
    }

    // Open modal if URL has modal=crm param (URL is source of truth)
    if (shouldOpenModal && fundraiser) {
      // For step 3 with Blackbaud connected, ensure CRM type is set
      if (isOnSyncToggleStep && isBlackbaudConnected) {
        const currentCrmType = fundraiser.get('crm')?._cls;
        if (currentCrmType !== 'BlackbaudCRM') {
          fundraiser.set({
            crm: { ...fundraiser.get('crm'), _cls: 'BlackbaudCRM' },
          });
        }
      } else if (crmType) {
        const currentCrmType = fundraiser.get('crm')?._cls;
        if (currentCrmType !== crmType) {
          fundraiser.set({
            crm: { ...fundraiser.get('crm'), _cls: crmType as 'BlackbaudCRM' | 'SalesforceCRM' },
          });
          return;
        }
      }
      // Only open if not already open at the correct step
      if (!isOpen || initialStep !== step) {
        openModal(step);
      }
    }
  }, [
    location.search,
    isOpen,
    initialStep,
    openModal,
    fundraiser,
    fundraiser?.get('crm')?._cls,
    isBlackbaudConnected,
    isBlackbaudLoading,
  ]);

  function getDescription(): JSX.Element {
    let content: ReactNode;
    if (crmActive) {
      // Blackbaud: Show sync status. Salesforce: Always show "Data sync active"
      if (isBlackbaudConnected) {
        content = isBlackbaudSyncActive ? t('Data sync active') : t('Connected');
      } else {
        content = t('Data sync active');
      }
    } else {
      content = (
        <>
          {t('Connect your CRM to sync donation data.')}
          <Button
            href={
              'https://help.feathr.co/hc/en-us/articles/36120664372375-How-to-Connect-Feathr-Fundraising-with-Salesforce'
            }
            target={'_blank'}
            type={'link'}
          >
            {t('Learn more about CRM integrations')}
          </Button>
        </>
      );
    }
    return <div className={styles.description}>{content}</div>;
  }

  function getPrefix(): JSX.Element {
    if (crmActive && crmVendor) {
      const cardIcon = logos[`${crmVendor}_card`];
      return (
        <img
          alt={t('{{- crmVendor}} logo', { crmVendor })}
          className={styles.icon}
          src={cardIcon}
        />
      );
    }
    return (
      <img
        alt={t('Database icon with three stacked discs on a light gray background.')}
        className={styles.icon}
        src={crmIcon}
      />
    );
  }

  function getTitle(): string {
    // Show CRM name when connected (Blackbaud or Salesforce)
    if (crmActive) {
      return getFormalNameCRM();
    }

    return t('Customer Relationship Manager');
  }

  function getTitleSuffix(): string | null {
    if (crmActive) {
      return null;
    }
    return t('(Optional)');
  }

  function getCRMButton(): JSX.Element {
    if (crmActive) {
      // Blackbaud shows "Manage" button linking to settings page
      if (isBlackbaudConnected) {
        return (
          <Button
            disabled={isPending}
            isLoading={isPending}
            onClick={(): void => {
              history.push(localUrl('/settings/integrations/raisers-edge'));
            }}
          >
            {t('Manage')}
          </Button>
        );
      }

      // Salesforce shows "Disconnect" button
      return (
        <Button disabled={isPending} isLoading={isPending} onClick={handleOnClickDisconnect}>
          {t('Disconnect')}
        </Button>
      );
    }

    const isDisabled = isPending || stripeState === 'not_connected' || !hasCRMLicense;
    const tooltipText = !hasCRMLicense ? t('Upgrade to Advanced to connect a CRM') : undefined;

    return (
      <Button
        disabled={isDisabled}
        onClick={(): void => {
          history.push(`${location.pathname}?modal=crm&step=0`);
        }}
        title={tooltipText}
      >
        {t('Connect CRM')}
      </Button>
    );
  }

  return (
    <Card name={'crm-connect-card'} width={'full'}>
      <Card.Header
        description={getDescription()}
        prefix={getPrefix()}
        title={getTitle()}
        titleSuffix={getTitleSuffix()}
      >
        <ConnectState state={crmState} />
        {getCRMButton()}
      </Card.Header>
    </Card>
  );
}

export default observer(CRMConnectCard);
